import { type FormEvent, useId, useState } from "react";
import { Clock, Mail, MapPin } from "lucide-react";
import { PrivacyCheckbox } from "@/components/legal/PrivacyCheckbox";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { buttonClass } from "@/components/ui/buttonStyles";
import { CONTACT_FORM_ENDPOINT, SOCIAL } from "@/config/constants";
import { useContent } from "@/data/content";
import { LEGAL_ADDRESS, LEGAL_CONTACT_EMAIL, LEGAL_MAPS_URL, LEGAL_PHONE } from "@/data/legal.types";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { trackEvent } from "@/utils/analytics";

const inputCls =
  "w-full rounded-xl border border-brand-primary/15 bg-white px-4 py-3 text-base text-ink outline-none transition-colors focus:border-teal-strong focus:ring-2 focus:ring-brand-secondary/40 aria-[invalid=true]:border-red-700";

type Status = "idle" | "submitting" | "success" | "error" | "invalid";

export function ContactSection() {
  const { contact } = useContent().home;
  const { t: tNav } = useAppTranslation("nav");
  const { t: tCommon } = useAppTranslation("common");
  const nameId = useId();
  const emailId = useId();
  const messageId = useId();
  const feedbackId = useId();
  const [status, setStatus] = useState<Status>("idle");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const submitting = status === "submitting";
  const invalid = status === "invalid" || status === "error";

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!privacyAccepted || submitting) return;
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
    if (name.length < 2 || name.length > 200 || !emailOk || message.length < 2 || message.length > 10000) {
      setStatus("invalid");
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(CONTACT_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, source: "contact" }),
      });
      if (!res.ok) throw new Error("send failed");
      trackEvent("contact_submit", { location: "contacto" });
      form.reset();
      setPrivacyAccepted(false);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section id="contacto" aria-labelledby="contacto-heading" className="bg-brand-bg">
      <div className="mx-auto grid max-w-7xl gap-14 px-4 py-24 sm:px-6 md:py-32 lg:grid-cols-[0.85fr_1.15fr] lg:items-start lg:gap-20 lg:px-8">
        <div>
          <SectionHeader id="contacto-heading" title={contact.title} lead={contact.lead} />
          <ul className="mt-10 space-y-4 text-base text-ink-soft">
            <li>
              <a
                href={SOCIAL.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-tracked=""
                onClick={() => trackEvent("whatsapp_click", { location: "contacto" })}
                className="inline-flex items-center gap-3 transition-colors hover:text-brand-primary"
              >
                <WhatsAppIcon className="size-4 shrink-0 text-teal-strong" />
                <span>
                  WhatsApp {LEGAL_PHONE}
                  <span className="sr-only"> {tCommon("opensNewTab")}</span>
                </span>
              </a>
            </li>
            <li>
              <a
                href={`mailto:${LEGAL_CONTACT_EMAIL}`}
                className="inline-flex items-center gap-3 break-all transition-colors hover:text-brand-primary"
              >
                <Mail aria-hidden="true" className="size-4 shrink-0 text-teal-strong" />
                {LEGAL_CONTACT_EMAIL}
              </a>
            </li>
            <li>
              <a
                href={LEGAL_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-start gap-3 transition-colors hover:text-brand-primary"
              >
                <MapPin aria-hidden="true" className="mt-1 size-4 shrink-0 text-teal-strong" />
                <address className="not-italic">
                  {LEGAL_ADDRESS.street}
                  <br />
                  {LEGAL_ADDRESS.postalCode} {LEGAL_ADDRESS.city}
                  <span className="sr-only"> {tCommon("opensNewTab")}</span>
                </address>
              </a>
            </li>
            <li className="inline-flex items-center gap-3">
              <Clock aria-hidden="true" className="size-4 shrink-0 text-teal-strong" />
              {tNav("hours")}
            </li>
          </ul>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          onInput={() => {
            if (status === "invalid" || status === "error" || status === "success") setStatus("idle");
          }}
          className="rounded-[2rem] border border-brand-primary/8 bg-white p-7 shadow-lit sm:p-9"
        >
          <div className="flex flex-col gap-4">
            <div>
              <label htmlFor={nameId} className="mb-1.5 block text-sm font-semibold text-brand-primary">
                {contact.name}
              </label>
              <input
                id={nameId}
                name="name"
                type="text"
                autoComplete="name"
                required
                minLength={2}
                maxLength={200}
                aria-invalid={status === "invalid"}
                aria-describedby={invalid || status === "success" ? feedbackId : undefined}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor={emailId} className="mb-1.5 block text-sm font-semibold text-brand-primary">
                {contact.email}
              </label>
              <input
                id={emailId}
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                aria-invalid={status === "invalid"}
                aria-describedby={invalid || status === "success" ? feedbackId : undefined}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor={messageId} className="mb-1.5 block text-sm font-semibold text-brand-primary">
                {contact.message}
              </label>
              <textarea
                id={messageId}
                name="message"
                required
                minLength={2}
                maxLength={10000}
                rows={5}
                placeholder={contact.messagePlaceholder}
                aria-invalid={status === "invalid"}
                aria-describedby={invalid || status === "success" ? feedbackId : undefined}
                className={`${inputCls} resize-y`}
              />
            </div>
            <PrivacyCheckbox
              checked={privacyAccepted}
              onChange={setPrivacyAccepted}
              disabled={submitting}
              suffix={contact.privacySuffix}
            />
            {status === "success" ? (
              <p id={feedbackId} role="status" className="text-sm font-medium text-teal-strong">
                {contact.success}
              </p>
            ) : null}
            {status === "invalid" ? (
              <p id={feedbackId} role="alert" className="text-sm font-medium text-red-700">
                {contact.validation}
              </p>
            ) : null}
            {status === "error" ? (
              <p id={feedbackId} role="alert" className="text-sm font-medium text-red-700">
                {contact.error}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting || !privacyAccepted}
              className={buttonClass("primary", "lg", "mt-2 w-full")}
            >
              {submitting ? tCommon("sending") : contact.submit}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
